// ChatModal.js
import React, { useRef, useState, useEffect } from 'react';
import {
  AudioOutlined,
  SendOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  DownloadOutlined,
  SyncOutlined
} from '@ant-design/icons';
import { Avatar, Button, Input, List, Modal, Space, Spin, message } from 'antd';
import { useAuth } from '../../../../auth/AuthContext';
import './ChatModal.css';
import botAvatar from '../../../../assets/images/bot-avatar.png';
import IconButtonGroup from '../../IconButtonGroup/IconButtonGroup';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { useDictation } from '../../../../context/DictationContext';

const initialMessages = [
  { id: 1, content: "Hello, What can I do for you?", sender: 'bot' },
];

const ChatModal = ({ isModalOpen, handleOk, handleCancel, title, fileId, patientId, type }) => {
  const [messages, setMessages] = useState(initialMessages);
  const [newMessage, setNewMessage] = useState('');
  const [expanded, setExpanded] = useState(false);
  const auth = useAuth();
  const userDetails = localStorage.getItem("userDetails");
  const user = userDetails ? JSON.parse(userDetails) : {};

  // Use the dictation context.
  const { isDictating, dictationStatus, interimTranscript, processedText, registerComponent, setActiveComponent, startDictation, stopDictation } = useDictation();
  const inputRef = useRef(null);
  // Create a unique ID for this component
  const componentId = useRef(`chatmodal-${fileId || Math.random().toString(36).substr(2, 9)}`);
  // Track cursor position
  const [cursorPosition, setCursorPosition] = useState({ start: 0, end: 0 });

  // Register the callback function that will be called when dictation text is ready
  useEffect(() => {
    // Define a callback that will insert text at the current cursor position in our input
    const handleDictationTextInsert = (text) => {
      // Update the newMessage state with the dictated text
      setNewMessage((prevMessage) => {
        // If there's no previous message, just use the dictated text
        if (!prevMessage) return text;
        
        // Insert the dictated text at the current cursor position
        const before = prevMessage.substring(0, cursorPosition.start);
        const after = prevMessage.substring(cursorPosition.end);
        const newText = before + text + after;
        
        // Update cursorPosition to be after the inserted text
        setTimeout(() => {
          if (inputRef.current) {
            const newPosition = before.length + text.length;
            inputRef.current.focus();
            inputRef.current.setSelectionRange(newPosition, newPosition);
          }
        }, 0);
        
        return newText;
      });
    };
    
    // Register our component with its callback
    registerComponent(componentId.current, handleDictationTextInsert);
  }, [registerComponent, cursorPosition]);

  // Handle text selection changes to track cursor position
  const handleSelectionChange = (e) => {
    const { selectionStart, selectionEnd } = e.target;
    setCursorPosition({ start: selectionStart, end: selectionEnd });
  };

  // Handle focus events to ensure this component is active when focused
  const handleInputFocus = () => {
    setActiveComponent(componentId.current);
  };

  // Watch for changes to processedText
  useEffect(() => {
    if (processedText) {
      // You can process the text here if needed
      // This effect could be left empty if you're using the callback approach only
    }
  }, [processedText]);

  useEffect(() => {
    const scrollToBottom = () => {
      const lastMessageElem = document.querySelector('.chat-list .ant-list-item:last-child');
      if (lastMessageElem) {
        lastMessageElem.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    };
    scrollToBottom();
    if (inputRef.current) {
      inputRef.current.focus(); // required so that we set the active component for the dictation and the user can continue typing without clicking again
    }
  }, [messages]);

  useEffect(() => {
    setMessages(initialMessages);
    setNewMessage('');
  }, [patientId]); // reset messages when patientId changes - not if you open and close the modal

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus(); // required so that we set the active component for the dictation
    }
  }, [isModalOpen]);

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    const question = newMessage;
    const userMsg = { id: messages.length + 1, content: question, sender: 'you' };

    const loadingMessageId = messages.length + 2;
    const loadingMessage = { id: loadingMessageId, content: "Loading bot's response...", sender: 'bot' };
    setMessages(prev => [...prev, userMsg, loadingMessage]);
    setNewMessage('');
    fetchBotResponse(question)
      .then(response => {
        setMessages(prev =>
          prev.map(msg => (msg.id === loadingMessageId ? { ...msg, content: response } : msg))
        );
      })
      .catch(error => {
        setMessages(prev =>
          prev.map(msg =>
            msg.id === loadingMessageId ? { ...msg, content: "Failed to load response" } : msg
          )
        );
      });
  };

  const fetchBotResponse = async (query) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/chatbot/qdrant/${patientId}/query?question=${encodeURIComponent(query)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${auth.user['access_token']}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        return data.error ? data.error : data.answer;
      }
    } catch (err) {
      return err.toString();
    }
  };

  const markdownToHtml = (markdown) => {
    const rawHtml = marked.parse(markdown);
    return DOMPurify.sanitize(rawHtml);
  };

  const handleExport = () => {
    let markdownContent = `# Chat Session Export\n\n`;
    messages.forEach(msg => {
      if (msg.sender === 'you') {
        markdownContent += `**You:** ${msg.content}\n\n`;
      } else {
        markdownContent += `**Copilot:** ${msg.content}\n\n`;
      }
    });
    const blob = new Blob([markdownContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'chat-export.md';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const modalTitle = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>{title}</span>
      <div style={{ marginRight: "20px" }}>
        <Button type="link" onClick={handleExport} icon={<DownloadOutlined />} />
        <Button type="link" onClick={() => setExpanded(!expanded)}>
          {expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      title={modalTitle}
      open={isModalOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      centered
      width={expanded ? 600 : 400}
      maskClosable={false}
      footer={null}
      style={{ right: 80, position: 'fixed', margin: 0, top: 150 }}
    >
      <div className="chat-container">
        <List
          className="chat-list"
          dataSource={messages}
          renderItem={item => (
            <List.Item key={item.id} className={`chat-message ${item.sender}`} style={{ padding: "10px", borderRadius: "10px" }}>
              {item.sender === 'bot' && (
                <Avatar src={botAvatar} style={{ backgroundColor: "#000000", padding: "5px" }} className="avatar-left" />
              )}
              <List.Item.Meta
                title={item.sender === 'you' ? 'You' : 'Copilot'}
                description={
                  item.content === "Loading bot's response..." ? <Spin /> :
                  item.sender === 'bot' ? (
                    <div>
                      {item.id !== 1 && (
                        <div style={{ textAlign: "right" }}>
                          
                          <IconButtonGroup
                            text={item.content}
                            feature={{ edit: false, copy: true, download: false, email: false }}
                            fileId={fileId}
                          />
                        </div>
                      )}
                      <div className="message-content" dangerouslySetInnerHTML={{ __html: markdownToHtml(item.content) }} />
                    </div>
                  ) : item.content
                }
              />
              {item.sender === 'you' && (
                <Avatar className="avatar-right" style={{ backgroundColor: "#1677FF" }}>
                  {`${user.first_name[0]}${user.last_name[0]}`}
                </Avatar>
              )}
            </List.Item>
          )}
        />
      </div>

      <Space.Compact style={{ width: '100%', marginTop: "10px" }}>
        <Button
          type="primary"
          onClick={() => {
            // For manual dictation toggle, if needed.
            if (isDictating) {
              stopDictation();
            } else {
              startDictation();
            }
          }}
          icon={<AudioOutlined />}
          danger={isDictating}
        />
        <Input
          ref={inputRef}
          value={newMessage}
          onChange={handleInputChange}
          onPressEnter={handleSendMessage}
          onFocus={handleInputFocus}
          onSelect={handleSelectionChange}
          onKeyUp={handleSelectionChange}
          onClick={handleSelectionChange}
          placeholder="Ask your query..."
        />
        <Button type="primary" onClick={handleSendMessage} icon={<SendOutlined />} />
      </Space.Compact>
      {(dictationStatus || isDictating || interimTranscript) && (
        <div style={{ marginTop: 10 }}>
          {dictationStatus && (
            <div style={{ padding: 8, borderRadius: 4, background: "#f0f0f0" }}>
              <b>Status:</b> {dictationStatus}
            </div>
          )}
          {isDictating && (
            <div style={{ color: "#52c41a" }}>
              <SyncOutlined spin /> Listening... (Speak clearly into your microphone)
            </div>
          )}
          {interimTranscript && (
            <div style={{ padding: 8, borderRadius: 4, background: "#e6f7ff", color: "#1890ff" }}>
              <b>Preview:</b> {interimTranscript}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default ChatModal;
